import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGlobalSlice } from "app/slice";
import { PhoneSelector } from "./phone-selector";
import { VLEMobileSimDialog } from "./vle-mobile-sim-dialog";
import { SimCardNumber } from "./sim-card-number";
import { toast } from "app/components/ui/use-toast";
import {
  getSingleNetworkSelectionData,
  saveSingleNetworkSelectionData,
} from "utils/purchase-flow-storage";

import { Button } from "app/components/ui/button";

type FormValue = {
  brand: string;
  model: string;
  simType: string;
  simNumber: string;
  imeiNumber: string;
};

interface VerificationResult {
  success: boolean;
  message: string;
  deviceId?: string;
  imei?: string;
}

interface OutletContext {
  isSimCardView: boolean;
  setIsSimCardView: React.Dispatch<React.SetStateAction<boolean>>;
  compatResult: any;
  setCompatResult: React.Dispatch<React.SetStateAction<any>>;
}
// changes

export const DeviceCompatibilty = () => {
  const [showVLESimDialog, setShowVLESimDialog] = useState(false);
  const savedData = getSingleNetworkSelectionData();
  const savedSimVerified = savedData?.deviceCompatibility?.simVerified || false;
  const savedImeiVerified =
    savedData?.deviceCompatibility?.imeiVerified || false;
  const [simVerified, setSimVerified] = useState(savedSimVerified);
  const [imeiVerified, setImeiVerified] = useState(savedImeiVerified);
  const { isSimCardView, setIsSimCardView, compatResult, setCompatResult } =
    useOutletContext<OutletContext>();
  const form = useForm<FormValue>({
    defaultValues: {
      brand: savedData?.deviceCompatibility?.brand || "",
      model: savedData?.deviceCompatibility?.model || "",
      simType: savedData?.deviceCompatibility?.simType || "",
      simNumber: savedData?.deviceCompatibility?.simNumber || "",
      imeiNumber: savedData?.deviceCompatibility?.imeiNumber || "",
    },
    mode: "onChange",
  });
  const dispatch = useDispatch();
  const { actions } = useGlobalSlice();

  const navigate = useNavigate();
  const { control, setValue, handleSubmit } = form;

  // Check if both SIM and IMEI are verified successfully
  const areSimAndImeiVerified = simVerified && imeiVerified;

  const onSubmit = async (data: FormValue) => {
    if (isSimCardView) {
      // Check if both SIM and IMEI are verified
      if (!data.simNumber || !data.imeiNumber) {
        toast({
          title: "Incomplete Verification",
          description:
            "Please verify both SIM and IMEI numbers before continuing",
          variant: "error",
        });
        return;
      }

      // Update compatibility data on successful verification
      const updatedCompatibilityData = {
        ...compatResult,
        deviceId: data.imeiNumber,
        enrollment_type: "HANDOVER",
        is_esim: data.simType === "eSim" ? "Y" : "N",
      };

      setCompatResult(updatedCompatibilityData);
      dispatch(actions.setCompatibilityData({ CD: updatedCompatibilityData }));
      // Navigate directly to number-type page after SIM verification
      navigate("/single-network-number-type");
    } else {
      const isPhysicalSim = data.simType === "sim";

      if (isPhysicalSim) {
        setShowVLESimDialog(true);
      } else {
        // For eSIM, update compatibility data immediately
        const updatedCompatibilityData = {
          ...compatResult,
          is_esim: "Y",
        };

        setCompatResult(updatedCompatibilityData);
        dispatch(
          actions.setCompatibilityData({ CD: updatedCompatibilityData })
        );
        navigate("/single-network-number-type");
      }
    }
  };

  const brand = useWatch({ control, name: "brand" });
  const model = useWatch({ control, name: "model" });
  const simType = useWatch({ control, name: "simType" });
  const simNumber = useWatch({ control, name: "simNumber" });
  const imeiNumber = useWatch({ control, name: "imeiNumber" });

  const deviceKeyRef = React.useRef<string | null>(null);
  // Reset SIM/IMEI verification when the user changes device (model or brand).
  useEffect(() => {
    if (!brand || !model) return;
    const key = `${brand}|${model}`;
    if (deviceKeyRef.current !== null && deviceKeyRef.current !== key) {
      setSimVerified(false);
      setImeiVerified(false);
      setValue("simNumber", "");
      setValue("imeiNumber", "");
    }
    deviceKeyRef.current = key;
  }, [brand, model, setValue]);

  // Save to sessionStorage when form values change
  useEffect(() => {
    saveSingleNetworkSelectionData({
      deviceCompatibility: {
        brand: brand || "",
        model: model || "",
        simType: simType || "",
        simNumber: simNumber || "",
        imeiNumber: imeiNumber || "",
        simVerified: simVerified,
        imeiVerified: imeiVerified,
      },
    });
  }, [brand, model, simType, simNumber, imeiNumber, simVerified, imeiVerified]);

  // Restore verification status and form values when switching to SIM card view
  useEffect(() => {
    if (isSimCardView) {
      const currentSavedData = getSingleNetworkSelectionData();
      const savedSimNumber = currentSavedData?.deviceCompatibility?.simNumber;
      const savedImeiNumber = currentSavedData?.deviceCompatibility?.imeiNumber;

      // Restore form values if they exist in session storage
      if (savedSimNumber) {
        setValue("simNumber", savedSimNumber);
      }
      if (savedImeiNumber) {
        setValue("imeiNumber", savedImeiNumber);
      }

      // Restore verification status
      if (currentSavedData?.deviceCompatibility?.simVerified) {
        setSimVerified(true);
      }
      if (currentSavedData?.deviceCompatibility?.imeiVerified) {
        setImeiVerified(true);
      }
    }
  }, [isSimCardView, setValue]);

  // Handle verification status changes from SimCardNumber component
  const handleVerificationChange = useCallback(
    (simVerified: boolean, imeiVerified: boolean) => {
      setSimVerified(simVerified);
      setImeiVerified(imeiVerified);
    },
    []
  );

  return (
    <React.Fragment>
      <FormProvider {...form}>
        <form
          className="w-full max-w-[31rem] md:mx-auto space-y-4 sm:space-y-5 lg:space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isSimCardView ? (
            <>
              <SimCardNumber onVerificationChange={handleVerificationChange} />
              {areSimAndImeiVerified && (
                <Button type="submit" variant="primary" className="w-full">
                  Continue
                </Button>
              )}
            </>
          ) : (
            <>
              <PhoneSelector
                imeiNumber={imeiNumber}
                control={control}
                setValue={setValue}
              />
              {brand && model && simType && (
                <Button type="submit" variant="primary">
                  Continue
                </Button>
              )}
            </>
          )}
        </form>
      </FormProvider>
      <VLEMobileSimDialog
        showDialog={showVLESimDialog}
        setShowDialog={setShowVLESimDialog}
        setView={setIsSimCardView}
        onNoClick={() => {
          // Clear form values when user selects "No"
          setValue("simNumber", "");
          setValue("imeiNumber", "");
          setSimVerified(false);
          setImeiVerified(false);
        }}
      />
    </React.Fragment>
  );
};
